import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { Card, Dialog, FormControl, IconButton, MenuItem, Select, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { SkuFormData } from "@/components/screens/ProductFormPage/types";
import { useProductFormStateContext } from "@/contexts/product-form-state-context";
import { getPublicImageUrl } from "@/helpers/image-url-helper";
import { IProductImage } from "@/interfaces/product-image.interface";
import { SkuFormMessages } from "@/messages/sku/sku-form.messages";
import { useSkuImageValidation } from "@/hooks/use-sku-image-validation";

type Props = {
  messages: SkuFormMessages;
};

export default function VariationImageSelector({ messages }: Props) {
  const { savedImages } = useProductFormStateContext();
  const { values, setFieldValue } = useFormikContext<SkuFormData>();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const { validateSkuImage } = useSkuImageValidation();

  // Limpar imagePhisicalAddress se a imagem selecionada não existir mais em savedImages
  useEffect(() => {
    if (values.imagePhisicalAddress && !validateSkuImage(values.imagePhisicalAddress)) {
      // Se tem apenas 1 imagem disponível, usar ela automaticamente
      if (savedImages.length === 1) {
        setFieldValue("imagePhisicalAddress", savedImages[0].phisicalAddress);
      } else {
        // Deixar vazio para usar imagem padrão
        setFieldValue("imagePhisicalAddress", "");
      }
    }
  }, [savedImages, values.imagePhisicalAddress, setFieldValue, validateSkuImage]);

  // Lógica de visibilidade: mostrar apenas se houver 2+ imagens
  if (savedImages.length < 2) {
    const count = savedImages.length;
    let infoMessage = "";

    if (count === 0) {
      infoMessage =
        "Nenhuma imagem cadastrada. Cadastre pelo menos 2 imagens no produto para poder selecionar uma imagem específica para este SKU.";
    } else {
      infoMessage = `Produto possui ${count} imagem. Cadastre pelo menos 2 imagens no produto para poder selecionar uma imagem específica para este SKU.`;
    }

    return (
      <Card
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "300px",
          backgroundColor: "grey.50",
        }}
      >
        <MDBox p={3} textAlign="center">
          <ImageNotSupportedIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
          <MDTypography variant="body2" color="text">
            {infoMessage}
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  const selectedImage = savedImages.find(
    (img) => img.phisicalAddress === values.imagePhisicalAddress
  );

  const handleImageChange = (phisicalAddress: string) => {
    setFieldValue("imagePhisicalAddress", phisicalAddress);
  };

  const handleImageClick = (phisicalAddress: string) => {
    setFieldValue("imagePhisicalAddress", phisicalAddress);
  };

  const handleOpenLightbox = () => {
    setIsLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setIsLightboxOpen(false);
  };

  const getImageUrl = (image: IProductImage) => {
    // 1️⃣ URL já transformada pelo hook
    if (image.url) {
      return image.url;
    }

    // 2️⃣ FALLBACK: preview local
    if (image.preview) {
      return image.preview;
    }

    // 3️⃣ FALLBACK: Gerar a partir de phisicalAddress
    if (image.phisicalAddress) {
      const generatedUrl = getPublicImageUrl(image.phisicalAddress);
      if (generatedUrl) {
        return generatedUrl;
      }
    }

    // 4️⃣ Placeholder final
    return "/placeholder.svg";
  };

  return (
    <MDBox>
      {/* Pré-visualização grande - Prioridade visual */}
      <Card
        sx={{
          position: "relative",
          mb: 3,
          height: "300px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "grey.50",
        }}
      >
        {selectedImage ? (
          <>
            <img
              src={getImageUrl(selectedImage)}
              alt={selectedImage.name}
              onError={(e) => {
                e.currentTarget.src = "/placeholder.svg";
              }}
              style={{
                width: "100%",
                height: "300px",
                objectFit: "contain",
                backgroundColor: "#f5f5f5",
              }}
            />
            <Tooltip title="Ver em tela cheia">
              <IconButton
                onClick={handleOpenLightbox}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(255,255,255,0.9)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,1)",
                  },
                }}
              >
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            <MDBox
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                p: 1.5,
                bgcolor: "rgba(0,0,0,0.7)",
              }}
            >
              <MDTypography variant="caption" color="white">
                {selectedImage.name}
              </MDTypography>
            </MDBox>
          </>
        ) : (
          <MDBox textAlign="center" p={3}>
            <ImageNotSupportedIcon sx={{ fontSize: 64, color: "grey.400", mb: 1 }} />
            <MDTypography variant="body2" color="text" fontWeight="medium">
              {messages.variationImageDefaultOption}
            </MDTypography>
          </MDBox>
        )}
      </Card>

      {/* Título e descrição */}
      <MDBox mb={2}>
        <MDTypography variant="subtitle2" fontWeight="bold" mb={0.5}>
          {messages.variationImageLabel}
        </MDTypography>
        <MDTypography variant="caption" color="text">
          {messages.variationImageHelp}
        </MDTypography>
      </MDBox>

      {/* Dropdown de seleção */}
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <Select
          value={values.imagePhisicalAddress || ""}
          onChange={(e) => handleImageChange(e.target.value)}
          displayEmpty
        >
          <MenuItem value="">
            <MDBox display="flex" alignItems="center" gap={1}>
              <ImageNotSupportedIcon fontSize="small" />
              <MDTypography variant="body2">{messages.variationImageDefaultOption}</MDTypography>
            </MDBox>
          </MenuItem>

          {savedImages.map((image) => {
            return (
              <MenuItem key={image.id} value={image.phisicalAddress}>
                <MDBox display="flex" alignItems="center" gap={1} width="100%">
                  <img
                    src={getImageUrl(image)}
                    alt={image.name}
                    onError={(e) => {
                      e.currentTarget.src = "/placeholder.svg";
                    }}
                    style={{
                      width: "40px",
                      height: "40px",
                      objectFit: "cover",
                      borderRadius: "4px",
                    }}
                  />
                  <MDBox flex={1}>
                    <MDTypography variant="body2">{image.name}</MDTypography>
                  </MDBox>
                </MDBox>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {/* Grid de miniaturas */}
      <Grid container spacing={1}>
        {savedImages.map((image) => {
          const isSelected = image.phisicalAddress === values.imagePhisicalAddress;

          return (
            <Grid size={{ xs: 4 }} key={image.id}>
              <Card
                onClick={() => handleImageClick(image.phisicalAddress)}
                sx={{
                  cursor: "pointer",
                  border: isSelected ? "2px solid" : "1px solid",
                  borderColor: isSelected ? "primary.main" : "grey.300",
                  position: "relative",
                  "&:hover": {
                    borderColor: "primary.main",
                    transform: "scale(1.05)",
                    transition: "all 0.2s",
                  },
                }}
              >
                <img
                  src={getImageUrl(image)}
                  alt={image.name}
                  onError={(e) => {
                    e.currentTarget.src = "/placeholder.svg";
                  }}
                  style={{
                    width: "100%",
                    height: "60px",
                    objectFit: "cover",
                  }}
                />
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Modal Lightbox */}
      <Dialog
        open={isLightboxOpen}
        onClose={handleCloseLightbox}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "rgba(0,0,0,0.95)",
          },
        }}
      >
        <MDBox p={2} display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          {selectedImage && (
            <img
              src={getImageUrl(selectedImage)}
              alt={selectedImage.name}
              onError={(e) => {
                e.currentTarget.src = "/placeholder.svg";
              }}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          )}
        </MDBox>
      </Dialog>
    </MDBox>
  );
}
